import { toast } from 'sonner'
import { XmlMappingConflictError } from '@/components/products/xmlImport.types'
import type {
  ImportJob,
  ImportResponse,
  ImportStatusDetails,
  XmlFieldMapping,
  XmlMappingConfirmation,
} from '@/services/products/types'
import {
  getErrorMessage,
  getFailedCount,
  getImportedCount,
  getImportStatusDetails,
} from '@/services/products/utils'
import { handleApiError } from '../api'
import { BaseService } from '../base/BaseService'

// A feed file is a real catalog source, just not through this path, so each
// message names the button that accepts that exact format.
export const XML_FEED_URL_ERROR =
  "That link is an XML product feed, not a store website. Download it and use Upload XML, or enter your store's homepage."

export const CSV_FEED_URL_ERROR =
  "That link is a CSV product feed, not a store website. Download it and use Upload CSV, or enter your store's homepage."

export const FILE_URL_ERROR =
  "That link points to a file, not a website. Enter your store's homepage. To add a document to your assistant's knowledge, use Sources."

// Each list is exactly what its button accepts. Mirrors
// Products::UrlImportService::FILE_URL_MESSAGES, which is the authoritative gate.
const FILE_URL_MESSAGES: Record<string, string> = Object.fromEntries([
  ...['xml', 'rss'].map((extension) => [extension, XML_FEED_URL_ERROR]),
  ...['csv', 'txt'].map((extension) => [extension, CSV_FEED_URL_ERROR]),
  ...[
    'json',
    'pdf',
    'zip',
    'gz',
    'tar',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'png',
    'jpg',
    'jpeg',
    'gif',
    'svg',
    'webp',
  ].map((extension) => [extension, FILE_URL_ERROR]),
])

function fileUrlError(url: string): string | null {
  let pathname: string

  try {
    pathname = new URL(url).pathname
  } catch {
    return null
  }

  // Ruby's File.extname ignores a trailing slash and a leading-dot filename.
  const segment = pathname.replace(/\/+$/, '').split('/').pop() ?? ''

  if (segment.lastIndexOf('.') < 1) return null

  return (
    FILE_URL_MESSAGES[segment.split('.').pop()?.toLowerCase() ?? ''] ?? null
  )
}

export interface ImportOptions {
  onSuccess?: (message: string) => void
  onError?: (error: string) => void
  onProgress?: (job: ImportJob) => void
  showToast?: boolean
  collectionId?: string
}

export interface ImportPollingOptions extends ImportOptions {
  onCollectionCreated?: (collectionId: string) => void
  onComplete?: (job: ImportJob) => void
  onUpdate?: (job: ImportJob) => void
  // Fires once when a URL import flips to `partially_complete` — the moment
  // the first 25 products land and become functionally usable. Polling
  // continues until terminal status.
  onPreviewReady?: (job: ImportJob) => void
  // Overrides the default completed/completed_with_errors/failed stop condition.
  isTerminal?: (job: ImportJob) => boolean
  // Checked before every poll tick; polling stops silently once true.
  signal?: AbortSignal
}

// The XML detector runs in the background; give up rather than poll forever.
const XML_DETECTION_TIMEOUT_MS = 60_000

class ImportService extends BaseService {
  protected readonly baseURL = '/products'

  /**
   * Upload and process CSV file
   */
  async uploadCsv(
    file: File,
    options: ImportOptions = {}
  ): Promise<ImportResponse> {
    const { onSuccess, onError, showToast = true, collectionId } = options

    const formData = new FormData()
    formData.append('file', file)
    if (collectionId) {
      formData.append('collection_id', collectionId)
    }

    try {
      const response = await this.apiClient.post(
        `${this.baseURL}/imports/csv`,
        formData
      )

      const result = { ...response.data, success: true }

      if (result.success && result.importJob) {
        const message = `Your file "${file.name}" is being processed. The new collection will appear shortly.`
        if (showToast) {
          toast.success('Upload Started', { description: message })
        }
        onSuccess?.(message)
      }

      return result
    } catch (error) {
      const apiError = handleApiError(error)
      const errorMessage = apiError.message || 'Upload failed'

      if (showToast) {
        toast.error('Upload Failed', { description: errorMessage })
      }
      onError?.(errorMessage)

      return {
        success: false,
        error: errorMessage,
      }
    }
  }

  /**
   * Import products from a website URL
   */
  async importFromUrl(
    url: string,
    options: ImportOptions = {}
  ): Promise<ImportResponse> {
    const { onSuccess, onError, showToast = true, collectionId } = options
    const rejection = fileUrlError(url)

    if (rejection) {
      if (showToast) {
        toast.error('Import Failed', { description: rejection })
      }

      onError?.(rejection)

      return { success: false, error: rejection }
    }

    try {
      const response = await this.apiClient.post(
        `${this.baseURL}/imports/url`,
        { url, collection_id: collectionId }
      )

      const result = { ...response.data, success: true }

      if (result.success && result.importJob) {
        const message = 'Scanning website for products...'
        if (showToast) {
          toast.success('Import Started', { description: message })
        }
        onSuccess?.(message)
      }

      return result
    } catch (error) {
      const apiError = handleApiError(error)
      const errorMessage = apiError.message || 'Import failed'

      if (showToast) {
        toast.error('Import Failed', { description: errorMessage })
      }
      onError?.(errorMessage)

      return {
        success: false,
        error: errorMessage,
      }
    }
  }

  /**
   * Upload and detect an XML/RSS product feed
   */
  async uploadXmlFeed(
    file: File,
    collectionId?: string,
    options: ImportOptions = {}
  ): Promise<ImportResponse> {
    const { onSuccess, onError, showToast = true } = options

    const formData = new FormData()
    formData.append('file', file)
    if (collectionId) {
      formData.append('collection_id', collectionId)
    }

    try {
      const response = await this.apiClient.post(
        `${this.baseURL}/imports/xml`,
        formData
      )

      const result = { ...response.data, success: true }

      if (result.success && result.importJob) {
        const message = `Analyzing "${file.name}" to find your product fields...`
        if (showToast) {
          toast.success('Upload Started', { description: message })
        }
        onSuccess?.(message)
      }

      return result
    } catch (error) {
      const apiError = handleApiError(error)
      const errorMessage = apiError.message || 'Upload failed'

      if (showToast) {
        toast.error('Upload Failed', { description: errorMessage })
      }
      onError?.(errorMessage)

      return {
        success: false,
        error: errorMessage,
      }
    }
  }

  /**
   * Confirm the catalog-field-to-XML-path mapping for a detected feed
   */
  async confirmFieldMapping(
    importJobId: string,
    mapping: XmlFieldMapping,
    options: ImportOptions = {}
  ): Promise<ImportResponse> {
    const { onSuccess, onError, showToast = true } = options

    try {
      const body: XmlMappingConfirmation = { mapping }
      const response = await this.apiClient.post(
        `${this.baseURL}/imports/${importJobId}/mapping`,
        body
      )

      const result = { ...response.data, success: true }

      if (result.success && result.importJob) {
        const message = 'Field mapping confirmed. Converting your feed...'
        if (showToast) {
          toast.success('Mapping Confirmed', { description: message })
        }
        onSuccess?.(message)
      }

      return result
    } catch (error) {
      const apiError = handleApiError(error)

      if (apiError.status === 409) {
        throw new XmlMappingConflictError(apiError.message)
      }

      const errorMessage = apiError.message || 'Failed to confirm field mapping'

      if (showToast) {
        toast.error('Mapping Failed', { description: errorMessage })
      }
      onError?.(errorMessage)

      return {
        success: false,
        error: errorMessage,
      }
    }
  }

  /**
   * Get import job status
   */
  async getImportJob(id: string): Promise<{ importJob: ImportJob }> {
    const response = await this.apiClient.get(`${this.baseURL}/imports/${id}`)
    return response.data
  }

  /**
   * Get latest import job for a collection
   */
  async getLatestImportJob(
    collectionId?: string
  ): Promise<{ importJob: ImportJob | null }> {
    const params = collectionId ? `?collection_id=${collectionId}` : ''
    const response = await this.apiClient.get(
      `${this.baseURL}/imports/latest${params}`
    )

    return response.data
  }

  /**
   * Poll import job status until completion
   */
  async pollImportStatus(
    id: string,
    options: ImportPollingOptions = {}
  ): Promise<ImportJob> {
    const {
      onUpdate,
      onComplete,
      onError,
      onPreviewReady,
      showToast = true,
      isTerminal = (job: ImportJob) =>
        ['completed', 'completed_with_errors', 'failed'].includes(job.status),
      signal,
    } = options

    return new Promise((resolve, reject) => {
      let previewFired = false

      const poll = async () => {
        if (signal?.aborted) return

        try {
          const response = await this.getImportJob(id)
          if (signal?.aborted) return

          const job = response.importJob

          onUpdate?.(job)

          // Preview milestone — fires once, polling continues until terminal.
          // Single-chunk and skip_preview imports never enter
          // partially_complete; the terminal-state branch handles those.
          if (!previewFired && job.status === 'partially_complete') {
            previewFired = true
            onPreviewReady?.(job)
          }

          if (isTerminal(job)) {
            const statusDetails = getImportStatusDetails(job)

            if (showToast) {
              this.showImportResultToast(job, statusDetails)
            }

            if (job.status === 'failed') {
              onError?.(statusDetails.description)
            } else {
              onComplete?.(job)
            }

            resolve(job)
          } else {
            // Continue polling
            setTimeout(poll, 2000) // 2 second interval
          }
        } catch (error) {
          if (signal?.aborted) return

          const errorMessage =
            'Unable to complete the import. Please try again.'
          if (showToast) {
            toast.error('Import Error', { description: errorMessage })
          }
          onError?.(errorMessage)
          reject(error)
        }
      }

      poll()
    })
  }

  /**
   * Race the detection poll against a timeout, clearing the timer on settlement either way.
   */
  private raceDetectionTimeout(controller: AbortController): {
    promise: Promise<never>
    clear: () => void
  } {
    let handle: ReturnType<typeof setTimeout> | null = null

    const promise = new Promise<never>((_resolve, reject) => {
      handle = setTimeout(() => {
        controller.abort()
        reject(
          new Error('Timed out waiting for the feed to finish being analyzed.')
        )
      }, XML_DETECTION_TIMEOUT_MS)
    })

    return {
      promise,
      clear: () => {
        if (handle) clearTimeout(handle)
      },
    }
  }

  /**
   * Lets a caller (e.g. a closed dialog) cut the detection wait short early.
   */
  private raceDetectionCancellation(signal?: AbortSignal): Promise<never> {
    return new Promise<never>((_resolve, reject) => {
      if (signal?.aborted) {
        reject(new Error('Detection cancelled'))
        return
      }
      signal?.addEventListener(
        'abort',
        () => reject(new Error('Detection cancelled')),
        { once: true }
      )
    })
  }

  private pollUntilDetected(
    importJobId: string,
    controller: AbortController
  ): Promise<ImportJob> {
    return new Promise((resolve, reject) => {
      this.pollImportStatus(importJobId, {
        showToast: false,
        signal: controller.signal,
        isTerminal: (job) =>
          job.readyForMapping === true || job.status === 'failed',
        onComplete: resolve,
        onError: (message) => reject(new Error(message)),
      }).catch(reject)
    })
  }

  /**
   * Wait for XML feed detection to finish, then resolve with the detailed job
   */
  async waitForDetection(
    importJobId: string,
    signal?: AbortSignal
  ): Promise<ImportJob> {
    const controller = new AbortController()

    const relayAbort = () => controller.abort()
    signal?.addEventListener('abort', relayAbort)

    const timeout = this.raceDetectionTimeout(controller)
    const cancellation = this.raceDetectionCancellation(signal)
    const detection = this.pollUntilDetected(importJobId, controller)

    try {
      return await Promise.race([detection, timeout.promise, cancellation])
    } finally {
      controller.abort()
      signal?.removeEventListener('abort', relayAbort)
      timeout.clear()
    }
  }

  /**
   * Show appropriate toast based on import result
   */
  private showImportResultToast(
    job: ImportJob,
    statusDetails: ImportStatusDetails
  ): void {
    const importedCount = getImportedCount(job)
    const failedCount = getFailedCount(job)

    if (job.status === 'failed') {
      const errorMsg = getErrorMessage(job)
      toast.error(statusDetails.title, {
        description: errorMsg || statusDetails.description,
        duration: 8000, // Longer duration for errors
      })
    } else if (job.status === 'completed_with_errors') {
      // Use warning toast for partial success
      toast.warning(statusDetails.title, {
        description: `${importedCount} imported, ${failedCount} failed. Check the details for more info.`,
        duration: 6000,
      })
    } else if (job.status === 'completed') {
      if (importedCount === 0 && failedCount > 0) {
        // Edge case: technically completed but no imports
        toast.error('Import Failed', {
          description: `All ${failedCount} items could not be processed.`,
          duration: 8000,
        })
      } else {
        toast.success(statusDetails.title, {
          description: statusDetails.description,
        })
      }
    }
  }

  /**
   * Handle complete import workflow including polling and collection management
   */
  async handleImportWorkflow(
    job: ImportJob,
    options: ImportPollingOptions & {
      onCollectionsRefresh?: () => Promise<void>
      onProductsRefresh?: (collectionId?: string) => Promise<void>
    } = {}
  ): Promise<{
    success: boolean
    collectionId?: string
    job?: ImportJob
  }> {
    const {
      onCollectionsRefresh,
      onProductsRefresh,
      onCollectionCreated,
      onError,
      showToast = true,
    } = options

    try {
      const finalJob = await this.pollImportStatus(job.id, {
        onUpdate: options.onProgress,
        showToast: false, // We'll handle toasts ourselves
        onPreviewReady: async (previewJob) => {
          // Surface the new collection + its first 25 products as soon as
          // the preview chunk lands. Polling continues for the remainder
          // and a second refresh fires when the import fully completes.
          if (!previewJob.collectionId) return

          if (onCollectionsRefresh) {
            await onCollectionsRefresh()
          }

          onCollectionCreated?.(previewJob.collectionId)

          if (onProductsRefresh) {
            await onProductsRefresh(previewJob.collectionId)
          }

          if (showToast) {
            const found =
              previewJob.estimatedCatalogSize ?? previewJob.totalItems ?? 0
            // totalItems is the post-cap count (whatever discovery actually
            // selected to import); estimatedCatalogSize is the pre-cap count
            // (what's actually on the site). When they differ, we sampled.
            const importing = previewJob.totalItems ?? found
            const wasCapped = found > importing
            const remaining = Math.max(importing - 25, 0)

            let description: string
            if (wasCapped) {
              description = `Importing a sample of ${importing.toLocaleString()} (we cap at ${importing.toLocaleString()} to keep things fast — connect a feed for your full catalog). First 25 are ready now.`
            } else if (remaining > 0) {
              description = `Your chat agent can use the first 25 now while we import the remaining ${remaining.toLocaleString()} in the background.`
            } else {
              description = 'Your chat agent can use these now.'
            }

            toast.success(
              found > 25
                ? `Found ${found.toLocaleString()} products — first 25 are ready`
                : 'First 25 products are ready',
              {
                description,
                duration: 6000,
              }
            )
          }
        },
      })

      const statusDetails = getImportStatusDetails(finalJob)
      const importedCount = getImportedCount(finalJob)
      const failedCount = getFailedCount(finalJob)
      const hasSuccessfulImports = importedCount > 0

      // Handle completed or completed_with_errors - both can have successful imports
      if (hasSuccessfulImports) {
        // Switch to the newly created collection and fetch products
        if (finalJob.collectionId) {
          // Refresh collections first to ensure we have the latest
          if (onCollectionsRefresh) {
            await onCollectionsRefresh()
          }

          // Notify about the new collection
          onCollectionCreated?.(finalJob.collectionId)

          // Fetch products for the new collection
          if (onProductsRefresh) {
            await onProductsRefresh(finalJob.collectionId)
          }
        } else {
          // Fallback: just refresh products
          if (onProductsRefresh) {
            await onProductsRefresh()
          }
        }

        if (showToast) {
          if (failedCount > 0) {
            toast.warning(statusDetails.title, {
              description: `${importedCount} imported, ${failedCount} failed.`,
              duration: 6000,
            })
          } else {
            toast.success(statusDetails.title, {
              description: statusDetails.description,
            })
          }
        }

        return {
          success: true,
          collectionId: finalJob.collectionId,
          job: finalJob,
        }
      }

      // Handle failed or no imports
      if (finalJob.status === 'failed' || importedCount === 0) {
        const errorMsg = getErrorMessage(finalJob) || statusDetails.description
        if (showToast) {
          toast.error('Import Failed', {
            description: errorMsg,
            duration: 8000,
          })
        }
        onError?.(errorMsg)
        return { success: false, job: finalJob }
      }

      return { success: false, job: finalJob }
    } catch {
      const errorMessage = 'Unable to complete the import. Please try again.'
      if (showToast) {
        toast.error('Import Error', { description: errorMessage })
      }
      onError?.(errorMessage)
      return { success: false }
    }
  }
}

export const importService = new ImportService()
